import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { videos } from '@/mocks/data';

function formatViews(n: number): string {
  if (n >= 10000) return (n / 10000).toFixed(1) + '万';
  return String(n);
}

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const article = useMemo(() => videos.find((v) => v.id === id), [id]);

  const related = useMemo(() => {
    if (!article) return [];
    return videos
      .filter((v) => v.category === article.category && v.id !== article.id)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
            <i className="ri-error-warning-line text-3xl text-secondary-400"></i>
          </div>
          <h1 className="text-xl font-semibold text-foreground-950 mb-2">视频未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该视频不存在或已被移除</p>
          <Link to="/" className="inline-flex px-6 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <Breadcrumb
        items={[
          { label: '视频分类', href: '/category/hot' },
          { label: article.categoryName, href: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <article className="w-full px-4 md:px-6 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative aspect-video rounded-lg overflow-hidden bg-background-200 mb-6">
            <img
              src={article.thumbnail}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer transition-all duration-300 hover:bg-primary-600 hover:scale-110">
                <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
              </div>
            </div>
            <span className="absolute bottom-3 right-3 px-3 py-1 text-xs font-medium text-background-50 bg-black/70 rounded-md whitespace-nowrap">
              {article.duration}
            </span>
          </div>

          <header className="mb-6">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3 leading-snug">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(article.views)} 次播放
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-folder-line"></i>
                <Link to={`/category/${article.category}`} className="text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                  {article.categoryName}
                </Link>
              </span>
              <span className="flex items-center gap-1 text-accent-500">
                <i className="ri-star-fill"></i>
                {article.rating} 分
              </span>
            </div>
          </header>

          <div className="flex flex-wrap gap-2 mb-6">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>

          <div className="prose prose-sm max-w-none mb-10">
            <h2 className="text-base font-semibold text-foreground-950 mb-3">内容简介</h2>
            <p className="text-sm text-foreground-600 leading-relaxed">{article.description}</p>
          </div>

          {related.length > 0 && (
            <section className="border-t border-background-200/70 pt-8">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-product-shop>
                {related.map((v) => (
                  <VideoCard
                    key={v.id}
                    id={v.id}
                    title={v.title}
                    slug={v.slug}
                    thumbnail={v.thumbnail}
                    duration={v.duration}
                    categoryName={v.categoryName}
                    views={v.views}
                    date={v.date}
                    rating={v.rating}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.description,
            image: article.thumbnail,
            datePublished: article.date,
            author: {
              '@type': 'Organization',
              name: '蜜桃传媒',
            },
            publisher: {
              '@type': 'Organization',
              name: '蜜桃传媒',
              url: 'https://www.mitaomedia.com',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://www.mitaomedia.com/article/${article.id}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.mitaomedia.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.categoryName,
                  item: `https://www.mitaomedia.com/category/${article.category}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                },
              ],
            },
          }),
        }}
      />
    </div>
  );
}